import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Link,
  Breadcrumbs, Chip
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useNotificationManager } from "../../hooks/useNotificationHook"; // Corrected import path

const NewsUpdatesPage: React.FC = () => {
  const navigate = useNavigate();

  const [activeCategory, setActiveCategory] = useState<number>(0); // Default to 'All' (typeId 0)

  const {
    useFetchNotificationTypes,
    useFetchNewsUpdates
  } = useNotificationManager(localStorage.getItem("is_user_logged_in") === "true");

  const { data: newsUpdates, isLoading, isError } = useFetchNewsUpdates(activeCategory);
  const { data: notificationTypes, isLoading: isTypesLoading, isError: isTypesError } = useFetchNotificationTypes();

  const handleCategoryClick = (categoryId: number) => {
    setActiveCategory(categoryId);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: "#f9f9f9", p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate("/")}
          sx={{ cursor: "pointer" }}
        >
          Home
        </Link>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate("/")}
          sx={{ cursor: "pointer" }}
        >
          About
        </Link>
        <Typography color="text.primary">News & Updates</Typography>
      </Breadcrumbs>

      <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f9f9f9", p: 3 }}>
        {/* Sidebar */}
        <Box
          sx={{
            position: "sticky",
            top: 16,
            width: "250px",
            mr: 4,
            alignSelf: "start",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
            borderRadius: 2,
            boxShadow: 3,
          }}
        >
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom color="primary.main">
              Categories
            </Typography>
            <List>
              {/* Handle loading and error states for notification types */}
              {isTypesLoading ? (
                <Typography>Loading categories...</Typography>
              ) : isTypesError ? (
                <Typography color="error">Error loading categories.</Typography>
              ) : (
                // Dynamically generate category list
                [ { id: 0, name: "All" }, ...(notificationTypes?.data || []) ].map((category) => (
                  <ListItem key={category.id} disablePadding>
                    <ListItemButton
                      onClick={() => handleCategoryClick(category.id)}
                      sx={{
                        backgroundColor: activeCategory === category.id ? "primary.light" : "transparent",
                        color: activeCategory === category.id ? "primary.main" : "inherit",
                        "&:hover": {
                          backgroundColor: "#D3D3D3",
                        },
                      }}
                    >
                      <ListItemText primary={category.name} />
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Box>

        {/* Main Content */}
        <Box sx={{ flex: 1, backgroundColor: "white", borderRadius: 2, boxShadow: 3, p: 3 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom color="primary.main">
            News and Updates
          </Typography>

          {/* Loading state for notifications */}
          {isLoading && <Typography variant="body1">Loading notifications...</Typography>}

          {/* Error state for notifications */}
          {isError && <Typography variant="body1" color="error">Error loading notifications.</Typography>}

          {/* Display filtered notifications */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="body1" gutterBottom>
              Below are the latest notifications related to category:
              {activeCategory === 0 ? (
                <Chip
                  label={"All"}
                  color="primary" // You can customize this based on the category type
                  sx={{ marginLeft: 1 }} // Space between the text and chip
                />
              ) : (
                <Chip
                  label={notificationTypes?.data.find(type => type.id === activeCategory)?.name}
                  color="primary" // You can customize this based on the category type
                  sx={{ marginLeft: 1 }} // Space between the text and chip
                />
              )}
            </Typography>
            <Divider sx={{ my: 2 }} />

            {newsUpdates?.data?.length === 0 || newsUpdates?.data === null ? (
              <Typography variant="body1" color="textSecondary">
                No notifications available for this category.
              </Typography>
            ) : (
              newsUpdates?.data?.map((notification: any) => (
                <Box
                  key={notification.id}
                  sx={{
                    mb: 2,
                    padding: 2,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 2,
                  }}
                >

                  <Typography variant="h6" fontWeight="bold" color="primary.main" display="flex" alignItems="center">
                    {notification.title}
                    <Divider orientation="vertical" flexItem sx={{ marginLeft: 1, marginRight: 1 }} />
                    <Chip
                      size="small"
                      label={notificationTypes?.data.find(type => type.id === notification.type_id)?.name}
                      color="primary"
                      sx={{ fontWeight: 30 }}
                    />
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {new Date(notification.created_at).toLocaleString()}
                  </Typography>
                  <Typography variant="body1">{notification.description}</Typography>
                </Box>
              ))
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default NewsUpdatesPage;
